import {
  Scoreboard,
  ScoreboardParticipant,
  ScoreboardParticipants,
  ScoreboardScore,
  ScoreboardScores
} from '@ps/isp-web-component-library';
import type { EnrichedParticipants } from '@ps/isp-web-services-types';
import type {
  AmericanFootballFixture,
  BaseballFixture,
  BasketballFixture,
  EsportsFixture,
  HandballFixture,
  IceHockeyFixture,
  SnookerFixture
} from '@ps/web-shared-generated-graphql/sports';
import type { EventTypeId } from '@ps/web-sports-enums';
import React from 'react';

import { getScoreCharacterLength } from '@swf/sports/utils/getScoreCharacterLength';

interface Props<T extends DefaultScoreboardFixture> {
  enrichedParticipants?: EnrichedParticipants;
  fixture?: T;
  eventTypeId: EventTypeId;
}

type DefaultScoreboardFixture =
  | AmericanFootballFixture
  | BaseballFixture
  | BasketballFixture
  | EsportsFixture
  | HandballFixture
  | IceHockeyFixture
  | SnookerFixture;

const getParticipantScore = <T extends DefaultScoreboardFixture>(
  participant: EnrichedParticipants[number],
  score: T['score'] | undefined
) => (participant.type === 'HOME' ? score?.home : score?.away);

const DefaultScoreboard = <T extends DefaultScoreboardFixture>({
  enrichedParticipants,
  fixture,
  eventTypeId
}: Props<T>) => {
  const [participantA, participantB] = enrichedParticipants ?? [];

  const currentScore = participantA.type && participantB.type ? fixture?.score : null;

  return (
    <Scoreboard variant="lite">
      <ScoreboardParticipants>
        <ScoreboardParticipant name={participantA.participant} />
        <ScoreboardParticipant name={participantB.participant} />
      </ScoreboardParticipants>
      <ScoreboardScores>
        <ScoreboardScore
          score={[
            getParticipantScore(participantA, currentScore) ?? null,
            getParticipantScore(participantB, currentScore) ?? null
          ]}
          scoreCharacterLength={getScoreCharacterLength(eventTypeId, [
            currentScore?.home ?? 0,
            currentScore?.away ?? 0
          ])}
        />
      </ScoreboardScores>
    </Scoreboard>
  );
};

export default DefaultScoreboard;
